<template>
  <b-row>
    <b-col lg="12" style="overflow: auto"> 
      <b-row>
        <b-col cols="12" lg="6" style="margin-bottom: 7px">
          <span>{{$t('application.commentInfo.comment')}}</span>
        </b-col>
      </b-row>
    </b-col>

    <b-col lg="12" style="overflow: auto"> 
      <b-row>
        <b-col cols="12" lg="12">
          <b-form-textarea
            ref="commentInputRef"
            v-model.trim="commentInput"
            :placeholder="$t('application.commentInfo.placeholder_input_comment')"
            rows="3"
            no-resize
          />
        </b-col>
      </b-row>
    </b-col>

    <b-col lg="12" style="overflow: auto; margin-top: 15px;">
      <b-row>
        <b-col cols="12" lg="6">
          <b-button
            variant="outline-primary"
            :disabled="!commentInput || isCommenting"
            block
            @click="handleAddComment('public')"
          >
            <b-spinner
              v-if="isCommenting && type === 'public'"
              variant="primary"
              small
            />
            <span>
              {{ $t(isCommenting && type === 'public' ? 'application.commentInfo.saving' : 'application.commentInfo.add_public_comment') }}
            </span>
          </b-button>
        </b-col>
        <b-col cols="12" lg="6">
          <b-button
            variant="outline-primary"
            :disabled="!commentInput || isCommenting"
            block
            @click="handleAddComment('private')"
          >
            <b-spinner
              v-if="isCommenting && type === 'private'"
              variant="primary"
              small
            />
            <span>
              {{ $t(isCommenting && type === 'private' ? 'application.commentInfo.saving' : 'application.commentInfo.add_private_comment') }}
            </span>
          </b-button>
        </b-col>
      </b-row>
    </b-col>

    <b-col v-if="notificationLength > 0" cols="4">
      <b-button
        variant="outline-primary"
        :disabled="isResolving"
        block
        style="margin-top: 7px"
        @click="handleResolveComments"
      >
        <b-spinner
          v-if="isResolving"
          variant="primary"
          small
        />
        <span>
          {{ $t('application.commentInfo.resolveComments') }}
        </span>
      </b-button>
    </b-col>

    <b-col cols="12" xl="12" lg="12" md="12" class="mt-2">
      <div style="max-height: 60vh; min-height: 15vh; overflow: auto">
        <div v-if="!comments || !comments.length" style="text-align: center; margin-top: 5vh">
          <span>
            <h5>{{ $t('application.commentInfo.noComment') }}</h5>
          </span>
        </div>
        <div v-else>
          <b-card v-for="(comment, index) in comments" :key="`comment-${index}`">
            <div class="d-flex align-items-baseline">
              <h5 style="text-transform: capitalize">
                {{ comment.fullName }}
              </h5>
              <small class="text-muted" style="margin-left: 15px">{{
                comment.date.toLocaleString('fi', {
                  month: 'numeric',
                  day: 'numeric',
                  hour: 'numeric',
                  minute: 'numeric',
                })
              }}</small>
              <b-badge
                pill
                :variant="comment.type === 'public' || comment.type === undefined ? 'light-success' : 'light-warning'"
                style="text-transform: none; margin-left: 12px">
                <span>{{$t(comment.type === 'public' || comment.type === undefined ? 'application.commentInfo.public' : 'application.commentInfo.private')}}</span>
              </b-badge>
            </div>

            <b-row>
              <pre class="commentTextPre">{{ comment.text }}</pre>
            </b-row>
          </b-card>
        </div>
      </div>
    </b-col>
  </b-row>
</template>

<script>
import { BCard, BRow, BCol, BFormTextarea, BButton, BSpinner, BBadge } from 'bootstrap-vue'
import { addCommentToApplication, fetchComment, resolveApplicationComments } from '@/shared/services/commentService'
import ToastificationContent from '@hw-ui/components/toastification/ToastificationContent.vue'
import { TOAST_VARIABLES } from '@hw-ui/components/toastification/constant'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    BFormTextarea,
    BButton,
    BSpinner,
    BBadge,
  },
  props: {
    userData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      processer: {},
      comments: [],
      commentInput: '',
      isCommenting: false,
      isResolving: false,
      type: null,
    }
  },
  computed: {
    commentsId() {
      return this.userData.commentsId ? this.userData.commentsId : ''
    },
    notificationLength() {
      return this.userData.applicationNotifications?.filter(item => item === 'user_application_comment')?.length
    }
  },
  beforeCreate() {
    this.$store.dispatch('appAuth/getCurrentUser')
      .then(res => {
        const user = JSON.parse(JSON.stringify(res))
        this.processer = {
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,

        }
      })
  },
  created() {
    if (this.commentsId) {
      fetchComment(this.commentsId).then(commentDoc => {
        if (commentDoc && commentDoc.comments) {
          // Map over the comments array and create a new array with the same properties as the original comments
          // but with the date property converted to a JavaScript Date object
          this.comments = commentDoc.comments
            .map(i => ({ ...i, date: i.date.toDate() }))
            // Sort the newly created array in descending order by the date property
            .sort((a, b) => b.date - a.date)
        }
      }).catch(() => {
        this.$toast({
            component: ToastificationContent,
            props: {
              title: this.$t('application.commentInfo.getCommentsError'),
              icon: TOAST_VARIABLES.icons.error,
              variant: TOAST_VARIABLES.variants.error,
            },
          })
      })
    }
  },
  methods: {
    async handleAddComment(type) {
      try {
        this.isCommenting = true
        this.type = type
        const newComment = await addCommentToApplication({
          id: this.userData.id,
          comment: {
            text: this.commentInput,
            userId: this.processer.id,
            fullName: `${this.processer.firstName} ${this.processer.lastName}`,
            type,
          },
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('application.commentInfo.addCommentSuccess'),
            icon: TOAST_VARIABLES.icons.success,
            variant: TOAST_VARIABLES.variants.success,
          },
        })
        this.comments.unshift({
          ...newComment,
          date: new Date(newComment.date)
        })
        this.commentInput = ''
        this.isCommenting = false
        this.type = null
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('application.commentInfo.addCommentError'),
            icon: TOAST_VARIABLES.icons.error,
            variant: TOAST_VARIABLES.variants.error,
          },
        })
      }
    },
    async handleResolveComments() {
      try {
        this.isResolving = true
        const newNotifications = await resolveApplicationComments({
          id: this.userData.id,
          processer: this.processer.id,
        })
        this.$toast({
          component: ToastificationContent,
          position: 'top-right',
          props: {
            title: this.$t('application.commentInfo.resolve_comment_success'),
            icon: TOAST_VARIABLES.icons.success,
            variant: TOAST_VARIABLES.variants.success,
          },
        })
        this.userData.applicationNotifications = newNotifications
      } catch (error) {
        this.$toast({
          component: ToastificationContent,
          props: {
            title: this.$t('application.commentInfo.resolveCommentError'),
            icon: TOAST_VARIABLES.icons.error,
            variant: TOAST_VARIABLES.variants.error,
          },
        })
      } finally {
        this.isResolving = false
      }
    },
  },
}
</script>

<style lang="scss" scoped>
  .commentTextPre{
    background: none;
    margin-left: 15px;
    max-width: 100%;
    white-space: pre-wrap;
    font-size: 14px;
    color: rgba(0, 0, 0, 0.8);
  }
</style>